<template>
  <div class="messenger-widget-welcome-view">
    <div
      class="view-header-bg"
      v-bind="viewHeaderBgProps"
    ></div>
    <div class="messenger-widget-welcome-view__alert">
      <slot name="alert" />
    </div>
    <widget-scroll
      max-height="100%"
      class="welcome-view-scroll"
      :with-shadow="false"
      @scroll="scrollHandler($event)"
      @resize-content-height="onResizeContentHeight($event)"
    >
      <div class="welcome-view-scroll__content">
        <div class="view-header">
          <div class="view-header__title-container">
            <div
              v-if="welcomeViewSettings.logoUrl"
              class="view-header__logo"
            >
              <img :src="welcomeViewSettings.logoUrl" />
            </div>
            <div class="view-header__title">
              <messenger-text-with-emoji
                :text="welcomeViewSettings.greeting"
                emoji-size="32px"
              />
            </div>
          </div>
          <div class="view-header__subtitle">
            <messenger-text-with-emoji
              :text="welcomeViewSettings.intro"
              emoji-size="15px"
            />
          </div>
        </div>

        <div
          class="messenger-widget-welcome-view__channels"
          :class="{
            'messenger-widget-welcome-view__channels--has-chat-channel': hasChatChannel,
          }"
        >
          <template v-if="hasChatChannel">
            <messenger-chat-block
              v-if="hasActiveChat && !isPreChatSubmitting"
              :chat-state="chatState!"
              :last-message="lastMessage"
              :number-of-new-messages="numberOfNewMessages"
              @back-to-chat="$emit('back-to-chat')"
            />
            <messenger-pre-chat-block
              v-else
              :pre-chat-view-settings="preChatViewSettings"
              :form-step="preChatFormStep"
              :is-submitting="isPreChatSubmitting"
              :message-field-height="sharedSettings.hasOnlyOneActiveChannel ? '104px' : '64px'"
              @change-form-step="$emit('pre-chat-change-form-step', $event)"
              @submit="$emit('pre-chat-submit')"
            />
          </template>

          <div v-if="isBackButtonVisible">
            <a
              class="back-button"
              href="#"
              tabindex="0"
              @click="$emit('pre-chat-change-form-step', 'message')"
            >
              <widget-icon
                name="tmi-chevron-left"
                size="15px"
                class="emphasize--text back-button__chevron"
              />
              <span v-if="sharedSettings.hasOnlyOneActiveChannel">{{ $t('messengerWidget.backToMessage') }}</span>
              <span v-else>{{ $t('messengerWidget.selectAnotherChannel') }}</span>
            </a>
          </div>

          <template v-else>
            <a
              v-for="channel in channelsWithoutChat"
              :key="channel.type"
              class="view-button"
              href="#"
              tabindex="0"
              @click="$emit('click-channel-button', channel.type)"
            >
              <div
                v-bind="getThemeIconProps('view-button__icon')"
                class="view-button__icon"
              >
                <messenger-channel-icon
                  :channel-type="channel.type"
                  size="16px"
                />
              </div>

              <div class="view-button__title">
                <messenger-text-with-emoji :text="channel.name" />
              </div>

              <div class="view-button__chevron">
                <widget-icon
                  name="tmi-chevron-right"
                  size="15px"
                  class="emphasize--text"
                />
              </div>
            </a>
          </template>

          <div v-if="!welcomeViewSettings.channels.length">
            <div class="view-button view-button--disabled">
              <div class="view-button__icon">
                <widget-icon
                  name="warning"
                  size="20px"
                  class="orange--text"
                />
              </div>

              <div class="view-button__body">
                <div class="view-button__title">{{ $t('messengerWidget.noActiveChannels.title') }}</div>
                <div class="view-button__description">{{ $t('messengerWidget.noActiveChannels.description') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </widget-scroll>

    <div
      ref="footerEl"
      class="messenger-widget-welcome-view__footer"
    >
      <widget-powered-by />
    </div>
  </div>
</template>

<script setup lang="ts">
import WidgetPoweredBy from '@widgets/shared/components/WidgetPoweredBy.vue'
import WidgetScroll from '@widgets/shared/components/WidgetScroll.vue'
import type {
  MessengerPreChatViewSettings,
  MessengerWelcomeViewSettings,
  PreChatFormStep,
} from '@widgets/messenger/types'
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import { useWidgetSharedSettings } from '@widgets/messenger/compositions/useWidgetSharedSettings'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import MessengerChannelIcon from '@widgets/messenger/components/MessengerChannelIcon.vue'
import MessengerPreChatBlock from '@widgets/messenger/components/MessengerPreChatBlock.vue'
import MessengerChatBlock from '@widgets/messenger/components/MessengerChatBlock.vue'
import { isChatIdleState, type MessengerChatState } from '@widgets/messenger/types/chat/chat'
import type { MessengerLastMessage } from '@widgets/messenger/types/chat/message'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import type { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'
import { computed, ref } from '@/composition/vue/compositionApi'
import { addPxToNumber } from '@/utils/string/addPxToNumber'

const { welcomeViewSettings, preChatFormStep, chatState } = defineProps<{
  welcomeViewSettings: MessengerWelcomeViewSettings
  preChatViewSettings: MessengerPreChatViewSettings
  preChatFormStep?: PreChatFormStep
  isPreChatSubmitting?: boolean
  chatState?: MessengerChatState
  numberOfNewMessages?: number
  lastMessage?: MessengerLastMessage
}>()

const emit = defineEmits<{
  (e: 'click-channel-button', channelType: WebWidgetChannelType): void
  (e: 'pre-chat-change-form-step', step: PreChatFormStep): void
  (e: 'pre-chat-submit'): void
  (e: 'back-to-chat'): void
  (e: 'resize', height: number): void
}>()

const sharedSettings = useWidgetSharedSettings()
const { getThemeBlockWithBackgroundProps, getThemeIconProps } = useWidgetTheme()

const hasChatChannel = computed(() => welcomeViewSettings.channels.some((channel) => channel.type === 'chat'))
const channelsWithoutChat = computed(() => welcomeViewSettings.channels.filter((channel) => channel.type !== 'chat'))
const isBackButtonVisible = computed(() => preChatFormStep !== 'message')
const hasActiveChat = computed(() => chatState && !isChatIdleState(chatState))

const HEADER_HEIGHT = 232
const headerHeightPx = addPxToNumber(HEADER_HEIGHT)
const headerBgBlockOffsetPx = ref('0px')
const footerEl = ref<HTMLElement | null>(null)

/**
 * Scroll overlay solution: move detached header background synchronously with the scroll
 */
const scrollHandler = (scrollTop: number) => {
  const value = scrollTop > HEADER_HEIGHT ? HEADER_HEIGHT : scrollTop
  headerBgBlockOffsetPx.value = `-${addPxToNumber(value)}`
}
const viewHeaderBgProps = computed(() => {
  const baseProps = getThemeBlockWithBackgroundProps('view-header-bg')

  return {
    ...baseProps,
    style: {
      ...baseProps.style,
      transform: `translateY(${headerBgBlockOffsetPx.value})`,
    },
  }
})

const getFooterHeight = (): number => footerEl.value?.offsetHeight ?? 0

const onResizeContentHeight = (contentHeight: number) => {
  const totalHeight = contentHeight + getFooterHeight()
  emit('resize', totalHeight)
}
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

$headerTitleLineHeight: 40px;
$headerTitleLinesCount: 2;
$headerSubtitleLineHeight: 20px;
$headerSubtitleLinesCount: 3;

.messenger-widget-welcome-view {
  overflow: hidden;
  background: $shade;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 20px;
  position: relative;

  &__alert {
    position: absolute;
    top: 0;
    width: 100%;
    padding: 12px;
  }

  &__channels {
    margin-top: -24px;
    padding-left: 32px;
    padding-right: 32px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 8px;

    &--has-chat-channel {
      margin-top: -36px;
    }
  }

  &__footer {
    margin-top: auto;
  }
}

.welcome-view-scroll {
  height: 100%;
  flex: 1 1 auto;
  min-height: 0;

  // Subpixel overflow compensation to avoid micro-scroll
  :deep(.widget-scroll__scroll) {
    max-height: calc(100% + 1px);
  }

  &__content {
    height: 100%;
    min-height: 451px;
    padding-bottom: 12px;
  }
}

.view-header-bg {
  @include widget-gradient-bg(view-header-bg--gradient);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: v-bind(headerHeightPx);
  pointer-events: none;
  will-change: transform;
  z-index: 0;
}

.view-header {
  padding: 28px 32px 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  color: $white;
  position: relative;
  z-index: 1;

  &__title-container {
    min-height: $headerTitleLineHeight * $headerTitleLinesCount;
    display: flex;
    flex-direction: column;
    justify-content: end;
  }

  &__title {
    @include truncate($headerTitleLinesCount);
    font-size: 32px;
    font-weight: $bold-font;
    line-height: $headerTitleLineHeight;
  }

  &__subtitle {
    @include truncate($headerSubtitleLinesCount);
    font-size: 15px;
    font-weight: $medium-font;
    line-height: $headerSubtitleLineHeight;
    height: $headerSubtitleLineHeight * $headerSubtitleLinesCount;
    opacity: 0.8;
  }

  &__logo {
    width: 32px;
    height: 32px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;

    & > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 999px;
    }
  }
}

.view-button {
  @include widget-block();
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  transition: all $transition-200ms-ease-in-out;

  &__icon {
    @include widget-color-variants();
    flex-shrink: 0;
    flex-grow: 0;
    background: $shade;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin-right: 12px;
  }

  &__title {
    @include truncate();
    flex-shrink: 1;
    flex-grow: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: $medium-font;
    line-height: 20px;
    color: $emphasize;
  }

  &__chevron {
    flex-grow: 0;
    align-self: center;
    margin-left: 12px;
    transition: all $transition-200ms-ease-in-out;
  }

  &:hover:not(&--disabled) {
    box-shadow: 0 4px 16px rgba($black, 0.16);
  }

  &--disabled {
    cursor: default;
  }
}

.back-button {
  @include widget-block();
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  min-height: 48px;
  color: $emphasize;
  font-size: 14px;
  font-weight: $medium-font;
  line-height: 20px;

  &:hover {
    box-shadow: 0 4px 16px rgba($black, 0.16);
  }

  &__chevron {
    color: $emphasize;
    flex-grow: 0;
    margin-right: 12px;
  }
}
</style>
